import { useEffect, useRef, useState } from "react";
import api, { API_BASE_URL } from "../../../../utils/api";
import { util } from "fabric";
import {
  Canvas,
  Image as FabricImage,
  Line,
  IText,
  Rect,
  Textbox,
  Circle,
  Ellipse,
  Polygon,
  Polyline,
  PencilBrush,
  Path,
  Text,
} from "fabric";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useImage from "use-image";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import {
  Delete as DeleteIcon,
  Brush as BrushIcon,
  Save as SaveIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  DownloadOutlined as DownloadIcon,
  GridOnOutlined as GridOnIcon,
  GridOffOutlined as GridOffIcon,
  Build as BuildIcon,
  MoveUp as MoveUpIcon,
  MoveDown as MoveDownIcon,
  FormatShapes as FormatShapesIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  TextFormat as TextFormatIcon,
  StrikethroughS as StrikethroughSIcon,
  TextFields as TextFieldsIcon,
  TextIncrease as TextIncreaseIcon,
  TextDecrease as TextDecreaseIcon,
  FormatLineSpacing as FormatLineSpacingIcon,
  AlignHorizontalCenter as AlignHorizontalCenterIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  FormatColorFill as FormatColorFillIcon,
  BorderColor as BorderColorIcon,
  FormatSize as FormatSizeIcon,
  Category as CategoryIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  LineWeight as LineWeightIcon,
  FormatColorReset as FormatColorResetIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";

const MAX_WIDTH = 1200; // set your max width
const MAX_HEIGHT = 900; // set your max height

const AnnotateCanvas = ({ file, onClose, requestId }) => {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [showTemplateBar, setShowTemplateBar] = useState(true);
  const [showAssetsPane, setShowAssetsPane] = useState(true);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPos, setToolbarPos] = useState({ top: 0, left: 0 });
  const [openFab, setOpenFab] = useState(null); // 'fab1', 'fab2', or null
  const [toolbarDirection, setToolbarDirection] = useState("up"); // 'up' or 'down'
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [activePlaceholder, setActivePlaceholder] = useState(null);
  const [showCanvasTools, setShowCanvasTools] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const gridLinesRef = useRef([]);
  const [showAddPopover, setShowAddPopover] = useState(false);
  const addPopoverRef = useRef(null);
  const [showImageInput, setShowImageInput] = useState(false);
  const fileInputRef = useRef(null);
  const [showShapeOptions, setShowShapeOptions] = useState(false);
  const [showSpecialOptions, setShowSpecialOptions] = useState(false);
  const [strokeDropdownOpen, setStrokeDropdownOpen] = useState(false);
  const [strokeSliderValue, setStrokeSliderValue] = useState(1);
  const [templateThumbs, setTemplateThumbs] = useState({});
  const addButtonRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const isLoadingRef = useRef(false);
  const [, forceRender] = useState(0);

  //add asset use effect
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addPopoverRef.current && !addPopoverRef.current.contains(e.target)) {
        setShowAddPopover(false);
      }
    };

    if (showAddPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddPopover]);

  const drawGrid = () => {
    if (!canvas) return;

    // Use actual canvas dimensions instead of MAX_WIDTH/MAX_HEIGHT
    const midX = canvas.getWidth() / 2;
    const midY = canvas.getHeight() / 2;

    // Remove any existing grid lines first
    gridLinesRef.current.forEach((line) => canvas.remove(line));
    gridLinesRef.current = [];

    // Create new lines
    const vLine = new Line([midX, 0, midX, canvas.getHeight()], {
      stroke: "#FFFFFF",
      strokeWidth: 1,
      selectable: false,
      evented: false,
      strokeDashArray: [5, 5],
    });

    const hLine = new Line([0, midY, canvas.getWidth(), midY], {
      stroke: "#FFFFFF",
      strokeWidth: 1,
      selectable: false,
      evented: false,
      strokeDashArray: [5, 5],
    });

    // Store references and add to canvas
    gridLinesRef.current = [vLine, hLine];
    canvas.add(vLine);
    canvas.add(hLine);
    canvas.requestRenderAll();
  };

  //show grid useeffect
  useEffect(() => {
    if (!canvas) return;

    if (showGrid) {
      drawGrid();
    } else {
      gridLinesRef.current.forEach((line) => canvas.remove(line));
      gridLinesRef.current = [];
      canvas.requestRenderAll();
    }
  }, [showGrid, canvas]);

  FabricImage.prototype.toObject = (function (toObject) {
    return function () {
      return {
        ...toObject.call(this),
        isBackground: this.isBackground || false, // include custom prop
      };
    };
  })(FabricImage.prototype.toObject);

  //Loads some default assets
  const [assets, setAssets] = useState([
    {
      name: "Text Box",
      type: "i-text",
      text: "Click to Edit",
      fontSize: 24,
      fontFamily: "Arial",
      fill: "#222",
    },
    {
      name: "Box",
      type: "rect",
      width: 100,
      height: 60,
      fill: "transparent",
      stroke: "red",
      strokeWidth: 5,
    },
    {
      name: "Circle",
      type: "circle",
      radius: 40,
      fill: "transparent", // No fill
      stroke: "red", // Border color
      strokeWidth: 5, // Border thickness
    },
    {
      name: "Arrow",
      type: "path",
      path: "M 0 0 L 80 0 L 80 -10 L 100 10 L 80 30 L 80 20 L 0 20 z",
      fill: "red",
      originX: "center",
      originY: "center",
      scaleX: 1,
      scaleY: 1,
    },
  ]);

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  };

  const applyTemplate = async (tpl) => {
    if (!canvas) return;
    canvas.clear();
    canvas.backgroundColor = "white";
    canvas.requestRenderAll();
    for (const shape of tpl.shapes) {
      if (shape.type === "text") {
        const wrapped = wrapText(
          shape.text,
          20,
          "Arial",
          MAX_WIDTH - shape.left - 20
        );

        const text = new IText(wrapped, {
          left: shape.left,
          top: shape.top,
          fontSize: 20,
          fill: "black",
        });

        canvas.add(text);
      } else if (shape.type === "image") {
        try {
          const htmlImg = await loadImage(shape.url);
          const fabricImg = new FabricImage(htmlImg, {
            left: shape.left,
            top: shape.top,
            scaleX: shape.width / htmlImg.width,
            scaleY: shape.height / htmlImg.height,
          });
          canvas.add(fabricImg);
          canvas.requestRenderAll();
        } catch (err) {
          console.warn("Image failed to load:", shape.url, err);
        }
      }
    }
  };

  const saveToAssets = () => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;

    const name = prompt("Enter a name for the saved asset:");
    if (!name) return;

    let clone;

    try {
      switch (obj.type) {
        case "i-text": {
          const { type, ...cleaned } = obj.toObject();
          clone = new IText(obj.text, { ...cleaned, left: 0, top: 0 });
          break;
        }

        case "textbox": {
          const { type, ...cleaned } = obj.toObject();
          clone = new Textbox(obj.text, { ...cleaned, left: 0, top: 0 });
          break;
        }

        case "text":
          const { type, ...cleaned } = obj.toObject();
          clone = new Text(obj.text, { ...cleaned, left: 0, top: 0 });
          break;

        case "rect":
          clone = new Rect({ ...obj.toObject(), left: 0, top: 0 });
          break;

        case "circle":
          clone = new Circle({ ...obj.toObject(), left: 0, top: 0 });
          break;

        case "ellipse":
          clone = new Ellipse({ ...obj.toObject(), left: 0, top: 0 });
          break;

        case "polygon":
          clone = new Polygon(obj.points, {
            ...obj.toObject(),
            left: 0,
            top: 0,
          });
          break;

        case "polyline":
          clone = new Polyline(obj.points, {
            ...obj.toObject(),
            left: 0,
            top: 0,
          });
          break;

        case "path":
          clone = new Path(obj.path, { ...obj.toObject(), left: 0, top: 0 });
          break;

        case "image":
          if (obj.getElement()) {
            clone = new FabricImage(obj.getElement(), {
              ...obj.toObject(),
              left: 0,
              top: 0,
              scaleX: obj.scaleX,
              scaleY: obj.scaleY,
            });
          }
          break;

        default:
          alert(`❌ Unsupported object type: ${obj.type}`);
          return;
      }

      if (clone) {
        const assetData = {
          name,
          type: obj.type,
        };

        if (obj.type === "i-text") {
          assetData.text = obj.text;
          assetData.fontSize = obj.fontSize;
          assetData.fontFamily = obj.fontFamily;
          assetData.fill = obj.fill;
        } else if (obj.type === "rect") {
          assetData.width = obj.width;
          assetData.height = obj.height;
          assetData.fill = obj.fill;
        } else if (obj.type === "circle") {
          assetData.radius = obj.radius;
          assetData.fill = obj.fill;
        } else if (obj.type === "image" && obj.getSrc) {
          assetData.url = obj.getSrc();
        }

        setAssets((prev) => [...prev, assetData]);
      } else {
        alert("⚠️ Could not duplicate the object.");
      }
    } catch (err) {
      console.error("❌ Error duplicating object:", err);
      alert("Something went wrong while saving the asset.");
    }
  };

  const addAssetToCanvas = async (asset) => {
    if (!canvas) return;

    let newObj;

    if (asset.type === "i-text") {
      newObj = new IText(asset.text, {
        left: 100,
        top: 100,
        fontSize: asset.fontSize || 20,
        fontFamily: asset.fontFamily || "Arial",
        fill: asset.fill || "black",
      });
    } else if (asset.type === "rect") {
      newObj = new Rect({
        left: 100,
        top: 100,
        width: asset.width || 80,
        height: asset.height || 40,
        fill: asset.fill || "transparent", // ✅ allow transparent fill
        stroke: asset.stroke || null, // ✅ support stroke
        strokeWidth: asset.strokeWidth || 1, // ✅ support stroke thickness
      });
    } else if (asset.type === "circle") {
      newObj = new Circle({
        left: 100,
        top: 100,
        radius: asset.radius || 30,
        fill: asset.fill || "transparent",
        stroke: asset.stroke || "red",
        strokeWidth: asset.strokeWidth || 5,
      });
    } else if (asset.type === "image" && asset.url) {
      // ... your existing image logic
    }
    // 🔹 ADD THIS for path objects
    else if (asset.type === "path") {
      newObj = new Path(asset.path, {
        left: 100,
        top: 100,
        fill: asset.fill || "red",
        originX: "center",
        originY: "center",
        scaleX: asset.scaleX || 1,
        scaleY: asset.scaleY || 1,
      });
    }

    if (newObj) {
      canvas.add(newObj);
      canvas.setActiveObject(newObj);
      canvas.requestRenderAll();

      if (activePlaceholder) {
        canvas.remove(activePlaceholder.icon);
        canvas.remove(activePlaceholder.rect);
        setActivePlaceholder(null);
      }
    }
  };

  const bringForward = () => {
    if (canvas) {
      const obj = canvas.getActiveObject();
      if (obj) canvas.bringObjectForward(obj, false); // bump up one layer
    }
  };

  const sendBackward = () => {
    if (canvas) {
      const obj = canvas.getActiveObject();
      if (obj) canvas.sendObjectBackwards(obj, false); // bump down one layer
    }
  };

  const deleteObject = () => {
    if (canvas) {
      const obj = canvas.getActiveObject();
      if (obj) canvas.remove(obj);
    }
  };

  const toggleStyle = (style) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    if (style === "bold") {
      obj.set("fontWeight", obj.fontWeight === "bold" ? "normal" : "bold");
    } else if (style === "italic") {
      obj.set("fontStyle", obj.fontStyle === "italic" ? "normal" : "italic");
    } else if (style === "underline") {
      obj.set("underline", !obj.underline);
    } else if (style === "strikethrough") {
      obj.set("linethrough", !obj.linethrough);
    }

    canvas.requestRenderAll();
    autoWrapActiveText();
  };

  const toggleCase = () => {
    const obj = canvas?.getActiveObject();

    const toTitleCase = (str) =>
      str.replace(
        /\w\S*/g,
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );

    if (!obj || obj.type !== "i-text") return;

    const current = obj.text || "";

    // Initialize original case
    if (obj._originalText === undefined) {
      obj._originalText = current;
      obj._caseMode = 0;
    }

    obj._caseMode = (obj._caseMode + 1) % 4;

    let next;

    switch (obj._caseMode) {
      case 1:
        next = obj._originalText.toUpperCase();
        break;
      case 2:
        next = obj._originalText.toLowerCase();
        break;
      case 3:
        next = toTitleCase(obj._originalText);
        break;
      default:
        next = obj._originalText;
        break;
    }

    obj.set("text", next);
    canvas.requestRenderAll();
    autoWrapActiveText();
  };

  const setFontFamily = (font) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    obj.set("fontFamily", font);
    canvas.requestRenderAll();
    autoWrapActiveText();
  };

  const adjustFontSize = (delta) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    const current = obj.fontSize || 20;
    const newSize = Math.max(2, current + delta);
    obj.set("fontSize", newSize);
    canvas.requestRenderAll();
    autoWrapActiveText();
  };

  const toggleFab = (fabId) => {
    setOpenFab((prev) => (prev === fabId ? null : fabId));
  };

  const FabButton = ({ onClick, icon }) => (
    <button
      onClick={onClick}
      className="w-14 h-14 rounded-full border-2 border-blue-600 text-black bg-transparent flex items-center justify-center shadow-lg hover:bg-blue-50 transition">
      {icon}
    </button>
  );

  const ActionButton = ({ icon, style, delay = 0, onClick }) => (
    <motion.button
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0 }}
      transition={{ type: "spring", stiffness: 500, damping: 30, delay }}
      className="w-12 h-12 rounded-full bg-white border border-gray-300 shadow flex items-center justify-center"
      style={style}
      onClick={onClick}>
      {icon}
    </motion.button>
  );

  const adjustLineHeight = (delta) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    const current = obj.lineHeight || 1.2;
    const newVal = Math.max(0.5, current + delta);
    obj.set("lineHeight", newVal);
    canvas.requestRenderAll();
  };

  const adjustCharSpacing = (delta) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    const current = obj.charSpacing || 0;
    obj.set("charSpacing", Math.max(0, current + delta));
    canvas.requestRenderAll();
  };

  const setTextboxBackground = (color) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    obj.set("backgroundColor", color);
    canvas.requestRenderAll();
  };

  const setTextboxBorder = (color) => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    obj.set({
      stroke: color,
      strokeWidth: 1.5, // or expose this as well
    });
    canvas.requestRenderAll();
  };

  const measureTextWidth = (
    text,
    fontSize,
    fontFamily,
    fontWeight = "normal",
    fontStyle = "normal"
  ) => {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
  };

  const wrapText = (
    text,
    fontSize,
    fontFamily,
    maxWidth,
    fontWeight = "normal",
    fontStyle = "normal"
  ) => {
    const words = text.split(" ");
    let line = "";
    let result = "";

    for (let i = 0; i < words.length; i++) {
      const testLine = line + (line ? " " : "") + words[i];
      const width = measureTextWidth(
        testLine,
        fontSize,
        fontFamily,
        fontWeight,
        fontStyle
      );

      if (width > maxWidth && line) {
        result += line + "\n";
        line = words[i];
      } else {
        line = testLine;
      }
    }

    result += line;
    return result;
  };

  const autoWrapActiveText = () => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== "i-text") return;

    const wrapped = wrapText(
      obj.text.replace(/\n/g, " "), // unwrap old breaks before re-wrapping
      obj.fontSize || 20,
      obj.fontFamily || "Arial",
      MAX_WIDTH - obj.left - 20,
      obj.fontWeight || "normal",
      obj.fontStyle || "normal"
    );

    obj.set("text", wrapped);
    canvas.requestRenderAll();
  };

  const handleDownloadPDF = () => {
    if (!canvas) return;

    // 1. Hide grid lines (if any)
    gridLinesRef.current.forEach((line) => canvas.remove(line));

    // 2. Deselect all objects and re-render to clean up
    canvas.discardActiveObject();
    canvas.renderAll();

    // 3. Convert canvas to data URL
    const dataURL = canvas.toDataURL({
      format: "png",
      multiplier: 2, // higher resolution
    });

    // 4. Create and export PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: [MAX_WIDTH, MAX_HEIGHT],
    });

    pdf.addImage(dataURL, "PNG", 0, 0, MAX_WIDTH, MAX_HEIGHT);
    pdf.save("canvas-output.pdf");

    // 5. Re-add grid lines if they were visible
    if (showGrid) {
      gridLinesRef.current.forEach((line) => canvas.add(line));
      canvas.renderAll();
    }
  };

  const clampObjectToCanvas = (obj) => {
    if (!obj) return;

    const padding = 4;
    const width = obj.getScaledWidth();
    const height = obj.getScaledHeight();

    if (obj.left < padding) obj.left = padding;
    if (obj.left + width > MAX_WIDTH - padding) {
      obj.left = MAX_WIDTH - width - padding;
    }

    if (obj.top < padding) obj.top = padding;
    if (obj.top + height > MAX_HEIGHT - padding) {
      obj.top = MAX_HEIGHT - height - padding;
    }
  };

  const motionProps = (delay) => ({
    initial: { opacity: 0, scale: 0 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0 },
    transition: { type: "spring", stiffness: 500, damping: 30, delay },
  });

  const updateToolbarPosition = (obj) => {
    if (!obj) return;

    const bound = obj.getBoundingRect(true);
    const MARGIN = 80;
    let centerX = bound.left + bound.width / 2 - 75;
    let offsetY;

    const isText = ["i-text", "textbox", "text"].includes(obj.type);
    const isShape = [
      "Rect",
      "Circle",
      "Ellipse",
      "Polygon",
      "Polyline",
      "Path",
      "Line",
    ].includes(obj.type);
    const expandUp = bound.top > MARGIN;

    offsetY = expandUp ? bound.top - 60 : bound.top + bound.height + 60;

    setToolbarDirection(expandUp ? "up" : "down");
    setToolbarPos({ left: centerX, top: offsetY });
  };

  const imgFilename = (file.file_url || '').split('/').pop();
  const imgUrl = `${API_BASE_URL}/request-images/${encodeURIComponent(imgFilename)}`;
  const [imgElement] = useImage(imgUrl, "anonymous");

  // Setup the canvas & background
  useEffect(() => {
    if (!canvasRef.current) return;

    const fabricCanvas = new Canvas(canvasRef.current, {
      width: MAX_WIDTH,
      height: MAX_HEIGHT,
      backgroundColor: "white",
      selection: true,
      preserveObjectStacking: true,
    });

    setCanvas(fabricCanvas);

    setTimeout(() => {
      if (canvasRef.current) canvasRef.current.focus();
    }, 50);

    const handleSelection = (e) => {
      const obj = e.selected?.[0];

      if (!obj) return;
      updateToolbarPosition(obj);
      const sw = obj.strokeWidth || 1;
      setStrokeSliderValue(sw);
      setShowToolbar(true);
      setOpenFab(null);
    };

    fabricCanvas.on("selection:created", (e) => {
      handleSelection(e);
    });

    fabricCanvas.on("selection:updated", (e) => {
      handleSelection(e);
    });

    fabricCanvas.on("selection:cleared", () => {
      setShowToolbar(false);
      setOpenFab(null); // also close any open FAB menus
    });

    const handleMoving = (e) => {
      const obj = e.target;
      if (obj) {
        clampObjectToCanvas(obj);
        requestAnimationFrame(() => updateToolbarPosition(obj));
      }
    };

    fabricCanvas.on("object:moving", handleMoving);
    fabricCanvas.on("object:scaling", handleMoving);
    fabricCanvas.on("object:rotating", handleMoving);

    fabricCanvas.on("object:added", () => {
      if (!isLoadingRef.current) setHasUnsavedChanges(true);
    });
    fabricCanvas.on("object:modified", () => {
      if (fabricCanvas.getActiveObject()?.isEditing) return;
      if (!isLoadingRef.current) setHasUnsavedChanges(true);
    });
    fabricCanvas.on("object:removed", () => {
      if (!isLoadingRef.current) setHasUnsavedChanges(true);
    });

    return () => {
      fabricCanvas.dispose();
    };
  }, []);

  // When canvas & image are ready, add background image
  useEffect(() => {
    if (!canvas || !imgElement) return;

    const fabricImg = new FabricImage(imgElement, {
      left: 0,
      top: 0,
      selectable: false,
      evented: false,
      isBackground: true,
    });

    const imgWidth = fabricImg.width || 0;
    const imgHeight = fabricImg.height || 0;

    let scale = 1;
    if (imgWidth > MAX_WIDTH || imgHeight > MAX_HEIGHT) {
      const widthScale = MAX_WIDTH / imgWidth;
      const heightScale = MAX_HEIGHT / imgHeight;
      scale = Math.min(widthScale, heightScale);
    }

    fabricImg.scale(scale);
    canvas.setWidth(imgWidth * scale);
    canvas.setHeight(imgHeight * scale);

    canvas.getObjects().forEach((obj) => canvas.remove(obj));
    canvas.add(fabricImg);
    canvas.sendObjectToBack(fabricImg);
    canvas.renderAll();

    // ✅ After resizing the canvas, re-clamp any existing objects
    setTimeout(() => {
      canvas.getObjects().forEach((obj) => {
        if (!obj.isBackground) {
          clampObjectToCanvas(obj);
        }
      });
      canvas.requestRenderAll();
    }, 50); // Small delay to ensure Fabric updates bounds
  }, [canvas, imgElement]);

  useEffect(() => {
    if (!canvas) return;

    const handleKeyDown = (e) => {
      const activeObj = canvas.getActiveObject();
      if (activeObj?.isEditing) return;

      if (e.key === "Delete" && activeObj) {
        e.preventDefault();
        canvas.remove(activeObj);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        setHasUnsavedChanges(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canvas]); // ✅ reruns after canvas is ready

  const buttonStyle = {
    width: "80px",
    height: "40px",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  };

  // Load shapes **only AFTER canvas & image are ready**
  useEffect(() => {
    if (canvas && imgElement) {
      handleLoadShapes();
    }
  }, [canvas, imgElement]);

  // 📌 Add Text
  const addText = (text = "New Text") => {
    if (!canvas) return;
    const itext = new IText(text, {
      left: 100,
      top: 100,
      fontSize: 24,
      fontFamily: "Arial",
      fill: "#222",
    });
    canvas.add(itext);
    canvas.setActiveObject(itext);
    canvas.requestRenderAll();
  };

  // 📌 Add Shape
  const addShape = (shape) => {
    if (!canvas) return;

    let newShape = null;

    switch (shape) {
      case "square":
        newShape = new Rect({
          left: 100,
          top: 100,
          width: 80,
          height: 80,
          fill: "#f87171",
        });
        break;
      case "rectangle":
        newShape = new Rect({
          left: 100,
          top: 100,
          width: 120,
          height: 60,
          fill: "#60a5fa",
        });
        break;
      case "circle":
        newShape = new Circle({
          left: 100,
          top: 100,
          radius: 40,
          fill: "#34d399",
        });
        break;
      case "ellipse":
        newShape = new Ellipse({
          left: 100,
          top: 100,
          rx: 60,
          ry: 40,
          fill: "#c084fc",
        });
        break;
      case "polygon": {
        const sides = parseInt(prompt("Enter number of sides (3-12):"), 10);
        if (!sides || sides < 3 || sides > 12) return;

        const radius = 50;
        const angleStep = (2 * Math.PI) / sides;
        const points = Array.from({ length: sides }, (_, i) => ({
          x: radius + radius * Math.cos(i * angleStep),
          y: radius + radius * Math.sin(i * angleStep),
        }));

        newShape = new Polygon(points, {
          left: 100,
          top: 100,
          fill: "#facc15",
        });
        break;
      }
      case "line":
        newShape = new Line([0, 0, 120, 0], {
          left: 100,
          top: 100,
          stroke: "#f87171",
          strokeWidth: 4,
        });
        break;
    }

    if (newShape) {
      canvas.add(newShape);
      canvas.setActiveObject(newShape);
      canvas.requestRenderAll();
    }
  };

  // 📌 Add Image from URL
  const addImageFromURL = (url) => {
    if (!canvas || !url) return;

    const img = new Image();
    img.crossOrigin = "anonymous"; // try CORS first
    img.src = url;

    img.onload = () => {
      const fabricImg = new FabricImage(img, {
        left: 100,
        top: 100,
        scaleX: 1,
        scaleY: 1,
      });
      canvas.add(fabricImg);
      canvas.setActiveObject(fabricImg);
      canvas.requestRenderAll();
    };

    img.onerror = () => {
      alert(
        "We are very sorry! This image cannot be loaded because the site does not allow cross-origin use.\n\n" +
          "Try using a different image host like:\n" +
          "- imgur.com\n" +
          "- postimages.org\n" +
          "- a link from your own server or Cloudinary."
      );
    };
  };

  // 📌 Add Image from File Upload
  const addImageFromFile = (file) => {
    if (!canvas || !file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const fabricImg = new FabricImage(img, {
          left: 100,
          top: 100,
          scaleX: 1,
          scaleY: 1,
        });
        canvas.add(fabricImg);
        canvas.setActiveObject(fabricImg);
        canvas.requestRenderAll();
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // 📌 Enable Freehand Drawing (Path)
  const startDrawingPath = () => {
    if (!canvas) return;
    canvas.isDrawingMode = true;

    if (!canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush = new PencilBrush(canvas);
    }
    canvas.freeDrawingBrush.color = "#60a5fa";
    canvas.freeDrawingBrush.width = 2;

    const finalizeDrawing = () => {
      canvas.isDrawingMode = false;
      const path = canvas._objects.pop();
      if (path) {
        canvas.add(path);
        canvas.setActiveObject(path);
        canvas.renderAll();
      }
      canvas.off("mouse:up", finalizeDrawing);
    };

    canvas.on("mouse:up", finalizeDrawing);
  };

  const toolBtnStyle = {
    pointerEvents: "auto",
    width: "3rem",
    height: "3rem",
    borderRadius: "9999px",
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const charSpacingBtnStyle = {
    ...toolBtnStyle,
    display: "flex",
    flexDirection: "row",
    gap: "2px",
  };

  const fabButtonStyle = {
    pointerEvents: "auto",
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "9999px",
    borderWidth: "2px",
    borderColor: "#2563eb",
    color: "black",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow:
      "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
  };

  //not used just reference/testing
  const handleSaveShapesToFile = () => {
    if (!canvas) return;

    const json = canvas.toJSON();

    // Remove any background image objects
    json.objects = json.objects.filter((obj) => !obj.isBackground);

    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "canvas-shapes.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  //not used just reference/testing
  const handleLoadShapesFromFile = (file) => {
    if (!canvas || !file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);

        canvas.loadFromJSON(jsonData, undefined, {}).then(() => {
          // ✅ Re-add the background image after shapes load
          if (imgElement) {
            const fabricImg = new FabricImage(imgElement, {
              left: 0,
              top: 0,
              selectable: false,
              evented: false,
              isBackground: true,
            });

            const imgWidth = fabricImg.width || 0;
            const imgHeight = fabricImg.height || 0;

            let scale = 1;
            if (imgWidth > MAX_WIDTH || imgHeight > MAX_HEIGHT) {
              const widthScale = MAX_WIDTH / imgWidth;
              const heightScale = MAX_HEIGHT / imgHeight;
              scale = Math.min(widthScale, heightScale);
            }

            fabricImg.scale(scale);
            canvas.add(fabricImg);
            canvas.sendObjectToBack(fabricImg);
            canvas.requestRenderAll();
          }
        });
      } catch (error) {
        console.error("❌ Failed to load shapes:", error);
      }
    };

    reader.readAsText(file);
  };

  const handleSaveShapes = async () => {
    if (!canvas) return;

    const json = canvas.toJSON();

    // Remove background objects
    json.objects = json.objects.filter((obj) => !obj.isBackground);

    try {
      console.log("json", json); // ✅ correct way
      console.log("json stringified", JSON.stringify(json, null, 2)); // ✅ to see full JSON

      await api.post("/violations-annotations/save", {
        request_id: requestId,
        file_id: file.id,
        data: json,
        created_by: 1,
      });
      setHasUnsavedChanges(false);
    } catch (err) {
      //console.error("❌ Failed to save shapes:", err);
    }
  };

  const handleLoadShapes = async () => {
    if (!canvas) return;

    try {
      isLoadingRef.current = true; // ✅ disable tracking

      const res = await api.get(`/violations-annotations/${requestId}/${file.id}`);
      const jsonData = res.data;
      if (!jsonData || !jsonData.objects) {
        isLoadingRef.current = false;
        return;
      }

      canvas.loadFromJSON(jsonData, undefined, {}).then(() => {
        // ✅ add background after shapes load
        if (imgElement) {
          const fabricImg = new FabricImage(imgElement, {
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
            isBackground: true,
          });

          const imgWidth = fabricImg.width || 0;
          const imgHeight = fabricImg.height || 0;

          let scale = 1;
          if (imgWidth > MAX_WIDTH || imgHeight > MAX_HEIGHT) {
            const widthScale = MAX_WIDTH / imgWidth;
            const heightScale = MAX_HEIGHT / imgHeight;
            scale = Math.min(widthScale, heightScale);
          }

          fabricImg.scale(scale);
          canvas.add(fabricImg);
          canvas.sendObjectToBack(fabricImg);
          canvas.requestRenderAll();
        }

        // ✅ ALWAYS reset clamp after everything is loaded
        canvas.off("object:moving");
        canvas.on("object:moving", (e) => {
          const obj = e.target;
          if (!obj) return;

          const padding = 4;
          const width = obj.getScaledWidth();
          const height = obj.getScaledHeight();
          const cw = canvas.getWidth(); // final width
          const ch = canvas.getHeight(); // final height

          if (obj.left < padding) obj.left = padding;
          if (obj.left + width > cw - padding) obj.left = cw - width - padding;

          if (obj.top < padding) obj.top = padding;
          if (obj.top + height > ch - padding) obj.top = ch - height - padding;

          requestAnimationFrame(() => updateToolbarPosition(obj));
        });

        // ✅ finished loading – reset unsaved flag
        setHasUnsavedChanges(false);
        isLoadingRef.current = false;
      });
    } catch (err) {
      console.error("❌ Failed to load shapes:", err);
      isLoadingRef.current = false;
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "#fff",
        width: "100%",
        height: "100%",
        overflow: "auto",
        position: "relative",
        backgroundColor: "transparent",
      }}>
      {/* Close Button */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          display: "flex",
          gap: "8px",
          zIndex: 1000,
        }}>
        <div style={{ display: "flex", gap: "8px" }}>
          {/* Save Button */}
          <IconButton
            onClick={handleSaveShapes}
            sx={{ bgcolor: "rgba(255,255,255,0.8)" }}
            title="Save Shapes">
            <SaveIcon />
          </IconButton>
        </div>

        {/* Close Button */}
        <IconButton
          onClick={() => {
            if (hasUnsavedChanges) {
              setShowUnsavedPopup(true);
            } else {
              onClose();
            }
          }}
          sx={{ bgcolor: "rgba(255,255,255,0.8)" }}
          title="Close">
          <CloseIcon />
        </IconButton>
      </div>

      <div style={{ marginTop: "2em", marginBottom: "2em" }}>
        {/* Canvas + Assets Pane */}
        <div
          id="canvasandassetspanewarapperdiv"
          style={{
            display: "flex",
            flexDirection: "row",
            position: "relative",
          }}>
          {/* Canvas */}
          <div
            id="canvasdiv"
            style={{
              position: "relative",
              border: "1px solid #333",
              overflow: "hidden",
              zIndex: "1",
            }}>
            <canvas
              ref={canvasRef}
              tabIndex={0}
              style={{ display: "block", outline: "none" }}
            />

            {/* Top-right canvas tools */}
            <div
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                display: "flex",
                gap: "0.75rem",
                transitionProperty: "opacity",
                transitionDuration: "300ms",
                zIndex: 50,
                opacity: showCanvasTools ? 1 : 0.3,
              }}
              onMouseEnter={() => setShowCanvasTools(true)}
              onMouseLeave={() => setShowCanvasTools(false)}>
              {/* Download Button */}
              <button
                style={{
                  width: "2.5rem",
                  height: "2.5rem",
                  borderRadius: "9999px",
                  backgroundColor: "white",
                  boxShadow:
                    "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s ease-in-out",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "white")
                }
                title="Download"
                onClick={handleDownloadPDF}>
                <DownloadIcon />
              </button>

              {/* Grid Toggle Button */}
              <button
                style={{
                  width: "2.5rem",
                  height: "2.5rem",
                  borderRadius: "9999px",
                  backgroundColor: "white",
                  boxShadow:
                    "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s ease-in-out",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "white")
                }
                title="Toggle Grid"
                onClick={() => setShowGrid((prev) => !prev)}>
                {showGrid ? <GridOnIcon /> : <GridOffIcon />}
              </button>
            </div>
          </div>

          {/* Assets Pane */}
          <div
            id="assetspanediv"
            style={{
              display: "flex",
              flexDirection: "row",
              border: activePlaceholder
                ? "2px solid #facc15"
                : "2px solid #ccc",
              boxShadow: activePlaceholder ? "0 0 10px #facc15" : "none",
              transition: "transform 0.3s ease-in-out",
              transform: showAssetsPane ? "translateX(0)" : "translateX(-85%)",
              width: "300px",
              height: `${(canvas?.height || MAX_HEIGHT) - 2}px`,
              overflowY: "auto",
              backgroundColor: "#f3f3f3",
              position: "relative",
              zIndex: "0",
            }}>
            {/* Add Button */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                width: "40px",
              }}>
              <div
                ref={addButtonRef}
                style={{
                  marginTop: "8px",
                  cursor: "pointer",
                  fontSize: "20px",
                }}
                title="Add Asset"
                onClick={() => {
                  if (addButtonRef.current) {
                    const rect = addButtonRef.current.getBoundingClientRect();
                    // Adjust for scroll position
                    setPopoverPos({
                      top: rect.top + window.scrollY,
                      left: rect.left + window.scrollX,
                    });
                  }
                  setShowAddPopover((v) => !v);
                }}>
                +
              </div>
            </div>

            {/* Pane Content */}
            <div style={{ flex: 1, padding: "16px" }}>
              <h2 style={{ fontWeight: "bold", marginBottom: "8px" }}>
                Assets
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                }}>
                {assets.map((asset, idx) => (
                  <div
                    key={idx}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      background: "#fff",
                    }}
                    onClick={() => addAssetToCanvas(asset)}>
                    {asset.type === "i-text" && (
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                        }}>
                        T
                      </div>
                    )}
                    {asset.type === "rect" && (
                      <div
                        style={{
                          width: "40px",
                          height: "24px",
                          backgroundColor: asset.fill || "transparent",
                          border: `${asset.strokeWidth || 1}px solid ${
                            asset.stroke || "#ccc"
                          }`,
                          boxSizing: "border-box",
                        }}
                      />
                    )}
                    {asset.type === "circle" && (
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: "transparent", // No fill
                          border: `${asset.strokeWidth || 5}px solid ${
                            asset.stroke || "red"
                          }`,
                        }}
                      />
                    )}
                    {asset.type === "path" && (
                      <svg
                        viewBox="0 0 120 40"
                        style={{
                          width: "48px",
                          height: "48px",
                          border: "1px solid #ccc",
                        }}>
                        <path
                          d="M 0 0 L 80 0 L 80 -10 L 100 10 L 80 30 L 80 20 L 0 20 Z"
                          fill={asset.fill || "red"}
                        />
                      </svg>
                    )}

                    {asset.type === "image" && (
                      <img
                        src={asset.url}
                        alt={asset.name}
                        style={{
                          width: "48px",
                          height: "40px",
                          objectFit: "cover",
                          border: "1px solid #ccc",
                        }}
                      />
                    )}
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        color: "#333",
                      }}>
                      {asset.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Toolbox Toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                width: "40px",
                background: "#f3f3f3",
              }}>
              <div
                style={{
                  marginTop: "8px",
                  cursor: "pointer",
                  fontSize: "20px",
                }}
                title="Toggle Toolbox"
                onClick={() => setShowAssetsPane((v) => !v)}>
                🛠
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <AnimatePresence>
            {showToolbar && (
              <motion.div
                key="toolbar"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  delay: 0.05,
                }}
                style={{
                  position: "absolute",
                  zIndex: 50,
                  pointerEvents: "none",
                  top: `${toolbarPos.top}px`,
                  left: `${toolbarPos.left}px`,
                  transform: `translate(-50%, ${
                    toolbarDirection === "up" ? "-100%" : "0"
                  })`,
                }}>
                <div
                  style={{
                    display: "flex",
                    gap: "2.5rem",
                  }}>
                  {/* FAB 1 - General object actions */}
                  <div
                    style={{
                      position: "relative",
                      left:
                        canvas?.getActiveObject()?.type === "image"
                          ? "60px"
                          : "0px",
                    }}>
                    {openFab !== "fab2" && (
                      <button
                        onClick={() => toggleFab("fab1")}
                        style={{
                          pointerEvents: "auto",
                          width: "3.5rem",
                          height: "3.5rem",
                          borderRadius: "9999px",
                          borderWidth: "2px",
                          borderColor: "#2563eb",
                          color: "black",
                          backgroundColor: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow:
                            "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                        }}>
                        <BuildIcon />
                      </button>
                    )}
                    <AnimatePresence>
                      {openFab === "fab1" && (
                        <>
                          <motion.button
                            onClick={saveToAssets}
                            style={{
                              pointerEvents: "auto",
                              width: "3rem",
                              height: "3rem",
                              borderRadius: "9999px",
                              backgroundColor: "white",
                              border: "1px solid #d1d5db",
                              boxShadow:
                                "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "absolute",
                              [toolbarDirection === "up"
                                ? "bottom"
                                : "top"]: 10,
                              left: -60,
                            }}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                              delay: 0.05,
                            }}>
                            <SaveIcon />
                          </motion.button>

                          <motion.button
                            onClick={bringForward}
                            style={{
                              pointerEvents: "auto",
                              width: "3rem",
                              height: "3rem",
                              borderRadius: "9999px",
                              backgroundColor: "white",
                              border: "1px solid #d1d5db",
                              boxShadow:
                                "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "absolute",
                              [toolbarDirection === "up"
                                ? "bottom"
                                : "top"]: 60,
                              left: -30,
                            }}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                              delay: 0.1,
                            }}>
                            <MoveUpIcon />
                          </motion.button>

                          <motion.button
                            onClick={sendBackward}
                            style={{
                              pointerEvents: "auto",
                              width: "3rem",
                              height: "3rem",
                              borderRadius: "9999px",
                              backgroundColor: "white",
                              border: "1px solid #d1d5db",
                              boxShadow:
                                "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "absolute",
                              [toolbarDirection === "up"
                                ? "bottom"
                                : "top"]: 60,
                              left: 30,
                            }}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                              delay: 0.15,
                            }}>
                            <MoveDownIcon />
                          </motion.button>

                          <motion.button
                            onClick={deleteObject}
                            style={{
                              pointerEvents: "auto",
                              width: "3rem",
                              height: "3rem",
                              borderRadius: "9999px",
                              backgroundColor: "white",
                              border: "1px solid #d1d5db",
                              boxShadow:
                                "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "absolute",
                              [toolbarDirection === "up"
                                ? "bottom"
                                : "top"]: 10,
                              left: 60,
                            }}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                              delay: 0.2,
                            }}>
                            <DeleteIcon />
                          </motion.button>
                        </>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* FAB 2 - Text formatting */}
                  {["i-text", "textbox", "text"].includes(
                    canvas?.getActiveObject()?.type
                  ) && (
                    <div style={{ position: "relative" }}>
                      {openFab !== "fab1" && (
                        <button
                          onClick={() => toggleFab("fab2")}
                          style={{
                            pointerEvents: "auto",
                            width: "3.5rem",
                            height: "3.5rem",
                            borderRadius: "9999px",
                            borderWidth: "2px",
                            borderColor: "#2563eb",
                            color: "black",
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow:
                              "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                            transition: "background-color 150ms ease-in-out",
                          }}>
                          <FormatShapesIcon />
                        </button>
                      )}

                      <AnimatePresence>
                        {openFab === "fab2" && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 25,
                            }}
                            style={{
                              position: "absolute",
                              [toolbarDirection === "up" ? "bottom" : "top"]:
                                "4rem",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: "1rem",
                              }}>
                              {[
                                {
                                  key: "bold",
                                  icon: <FormatBoldIcon />,
                                  onClick: () => toggleStyle("bold"),
                                  delay: 0.05,
                                },
                                {
                                  key: "italic",
                                  icon: <FormatItalicIcon />,
                                  onClick: () => toggleStyle("italic"),
                                  delay: 0.1,
                                },
                                {
                                  key: "underline",
                                  icon: <TextFormatIcon />,
                                  onClick: () => toggleStyle("underline"),
                                  delay: 0.15,
                                },
                                {
                                  key: "strikethrough",
                                  icon: <StrikethroughSIcon />,
                                  onClick: () => toggleStyle("strikethrough"),
                                  delay: 0.2,
                                },
                                {
                                  key: "case",
                                  icon: <TextFieldsIcon />,
                                  onClick: toggleCase,
                                  delay: 0.25,
                                },
                                {
                                  key: "increaseFont",
                                  icon: <TextIncreaseIcon />,
                                  onClick: () => adjustFontSize(2),
                                  delay: 0.3,
                                },
                                {
                                  key: "decreaseFont",
                                  icon: <TextDecreaseIcon />,
                                  onClick: () => adjustFontSize(-2),
                                  delay: 0.35,
                                },
                                {
                                  key: "lineHeightUp",
                                  icon: <FormatLineSpacingIcon />,
                                  onClick: () => adjustLineHeight(0.2),
                                  delay: 0.05,
                                },
                                {
                                  key: "lineHeightDown",
                                  icon: <FormatLineSpacingIcon />,
                                  onClick: () => adjustLineHeight(-0.2),
                                  delay: 0.1,
                                },
                                {
                                  key: "charSpacingUp",
                                  type: "charSpacingUp",
                                  delay: 0.15,
                                },
                                {
                                  key: "charSpacingDown",
                                  type: "charSpacingDown",
                                  delay: 0.2,
                                },
                                {
                                  key: "bgColor",
                                  icon: <FormatColorFillIcon />,
                                  type: "color",
                                  inputId: "bgColorInput",
                                  onChange: setTextboxBackground,
                                  delay: 0.25,
                                },
                                {
                                  key: "borderColor",
                                  icon: <BorderColorIcon />,
                                  type: "color",
                                  inputId: "borderColorInput",
                                  onChange: setTextboxBorder,
                                  delay: 0.3,
                                },
                                {
                                  key: "fontSelector",
                                  icon: <FormatSizeIcon />,
                                  type: "font",
                                  delay: 0.35,
                                },
                              ].map(
                                ({
                                  key,
                                  icon,
                                  onClick,
                                  delay,
                                  type,
                                  inputId,
                                  onChange,
                                }) => {
                                  // 🔹 Char Spacing Up
                                  if (type === "charSpacingUp") {
                                    return (
                                      <motion.button
                                        key={key}
                                        onClick={() => adjustCharSpacing(50)}
                                        style={charSpacingBtnStyle}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <AlignHorizontalCenterIcon
                                          style={{ fontSize: "1rem" }}
                                        />
                                        <AddIcon
                                          style={{ fontSize: "0.875rem" }}
                                        />
                                      </motion.button>
                                    );
                                  }

                                  // 🔹 Char Spacing Down
                                  if (type === "charSpacingDown") {
                                    return (
                                      <motion.button
                                        key={key}
                                        onClick={() => adjustCharSpacing(-50)}
                                        style={charSpacingBtnStyle}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <AlignHorizontalCenterIcon
                                          style={{ fontSize: "1rem" }}
                                        />
                                        <RemoveIcon
                                          style={{ fontSize: "0.875rem" }}
                                        />
                                      </motion.button>
                                    );
                                  }

                                  // 🔹 Color Picker
                                  if (type === "color") {
                                    return (
                                      <motion.div
                                        key={key}
                                        style={{
                                          position: "relative",
                                          pointerEvents: "auto",
                                        }}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <button
                                          onClick={() =>
                                            document
                                              .getElementById(inputId)
                                              ?.click()
                                          }
                                          style={toolBtnStyle}>
                                          {icon}
                                        </button>
                                        <input
                                          id={inputId}
                                          type="color"
                                          style={{
                                            position: "absolute",
                                            inset: 0,
                                            opacity: 0,
                                            cursor: "pointer",
                                          }}
                                          onChange={(e) =>
                                            onChange(e.target.value)
                                          }
                                        />
                                      </motion.div>
                                    );
                                  }

                                  // 🔹 Font Selector
                                  if (type === "font") {
                                    return (
                                      <motion.div
                                        key={key}
                                        style={{
                                          position: "relative",
                                          pointerEvents: "auto",
                                        }}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <button
                                          onClick={() =>
                                            setFontDropdownOpen((v) => !v)
                                          }
                                          style={toolBtnStyle}>
                                          {icon}
                                        </button>

                                        {fontDropdownOpen && (
                                          <select
                                            onChange={(e) => {
                                              setFontFamily(e.target.value);
                                              setFontDropdownOpen(false);
                                            }}
                                            value={
                                              canvas?.getActiveObject()
                                                ?.fontFamily || ""
                                            }
                                            style={{
                                              position: "absolute",
                                              top: "3.5rem",
                                              left: 0,
                                              zIndex: 50,
                                              background: "white",
                                              color: "black",
                                              border: "1px solid #9ca3af",
                                              borderRadius: "0.25rem",
                                              padding: "0.25rem 0.5rem",
                                              fontSize: "0.875rem",
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.1)",
                                            }}>
                                            <option value="Arial">Arial</option>
                                            <option value="Georgia">
                                              Georgia
                                            </option>
                                            <option value="Courier New">
                                              Courier New
                                            </option>
                                            <option value="Times New Roman">
                                              Times New Roman
                                            </option>
                                            <option value="Verdana">
                                              Verdana
                                            </option>
                                          </select>
                                        )}
                                      </motion.div>
                                    );
                                  }

                                  // 🔹 Default Buttons
                                  return (
                                    <motion.button
                                      key={key}
                                      onClick={onClick}
                                      style={toolBtnStyle}
                                      initial={{ opacity: 0, scale: 0 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0 }}
                                      transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 30,
                                        delay,
                                      }}>
                                      {icon}
                                    </motion.button>
                                  );
                                }
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* FAB 3 - Shape Controls */}
                  {[
                    "rect",
                    "circle",
                    "ellipse",
                    "polygon",
                    "polyline",
                    "path",
                    "line",
                  ].includes(canvas?.getActiveObject()?.type) && (
                    <div style={{ position: "relative" }}>
                      {openFab !== "fab1" && openFab !== "fab2" && (
                        <button
                          onClick={() => toggleFab("fab3")}
                          style={fabButtonStyle}>
                          <CategoryIcon />
                        </button>
                      )}

                      <AnimatePresence>
                        {openFab === "fab3" && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 25,
                            }}
                            style={{
                              position: "absolute",
                              [toolbarDirection === "up" ? "bottom" : "top"]:
                                "4rem",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: "1rem",
                              }}>
                              {[
                                {
                                  key: "fillColor",
                                  icon: <FormatColorFillIcon />,
                                  type: "color",
                                  inputId: "shapeFillInput",
                                  onChange: (value) => {
                                    const obj = canvas?.getActiveObject();
                                    if (obj) {
                                      obj.set("fill", value);
                                      canvas.requestRenderAll();
                                    }
                                  },
                                  delay: 0.05,
                                },
                                {
                                  key: "clearFill",
                                  icon: <FormatColorResetIcon />,
                                  onClick: () => {
                                    const obj = canvas?.getActiveObject();
                                    if (obj) {
                                      obj.set("fill", "transparent");
                                      canvas.requestRenderAll();
                                    }
                                  },
                                  delay: 0.08,
                                },
                                {
                                  key: "strokeColor",
                                  icon: <BorderColorIcon />,
                                  type: "color",
                                  inputId: "shapeStrokeInput",
                                  onChange: (value) => {
                                    const obj = canvas?.getActiveObject();
                                    if (obj) {
                                      obj.set({
                                        stroke: value,
                                        strokeWidth: obj.strokeWidth || 2,
                                      });
                                      canvas.requestRenderAll();
                                    }
                                  },
                                  delay: 0.1,
                                },
                                {
                                  key: "rotateLeft",
                                  icon: <RotateLeftIcon />,
                                  onClick: () => {
                                    const obj = canvas?.getActiveObject();
                                    if (obj) {
                                      obj.rotate((obj.angle || 0) - 15);
                                      canvas.requestRenderAll();
                                    }
                                  },
                                  delay: 0.15,
                                },
                                {
                                  key: "rotateRight",
                                  icon: <RotateRightIcon />,
                                  onClick: () => {
                                    const obj = canvas?.getActiveObject();
                                    if (obj) {
                                      obj.rotate((obj.angle || 0) + 15);
                                      canvas.requestRenderAll();
                                    }
                                  },
                                  delay: 0.2,
                                },
                                {
                                  key: "strokeWidth",
                                  icon: <LineWeightIcon />,
                                  type: "dropdown",
                                  delay: 0.25,
                                },
                              ].map(
                                ({
                                  key,
                                  icon,
                                  onClick,
                                  delay,
                                  type,
                                  inputId,
                                  onChange,
                                }) => {
                                  // 🔹 Color Picker Buttons
                                  if (type === "color") {
                                    return (
                                      <motion.div
                                        key={key}
                                        style={{
                                          position: "relative",
                                          pointerEvents: "auto",
                                        }}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <button
                                          onClick={() =>
                                            document
                                              .getElementById(inputId)
                                              ?.click()
                                          }
                                          style={toolBtnStyle}>
                                          {icon}
                                        </button>
                                        <input
                                          type="color"
                                          id={inputId}
                                          style={{
                                            position: "absolute",
                                            inset: 0,
                                            opacity: 0,
                                            cursor: "pointer",
                                          }}
                                          onChange={(e) =>
                                            onChange(e.target.value)
                                          }
                                        />
                                      </motion.div>
                                    );
                                  }

                                  // 🔹 Stroke Width Dropdown
                                  if (key === "strokeWidth") {
                                    return (
                                      <motion.div
                                        key={key}
                                        style={{
                                          position: "relative",
                                          pointerEvents: "auto",
                                        }}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{
                                          type: "spring",
                                          stiffness: 500,
                                          damping: 30,
                                          delay,
                                        }}>
                                        <button
                                          onClick={() =>
                                            setStrokeDropdownOpen((v) => !v)
                                          }
                                          style={toolBtnStyle}>
                                          {icon}
                                        </button>

                                        {strokeDropdownOpen && (
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "3.5rem",
                                              left: "50%",
                                              transform: "translateX(-50%)",
                                              width: "8rem",
                                              background: "white",
                                              border: "1px solid #9ca3af",
                                              padding: "0.5rem",
                                              borderRadius: "0.25rem",
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.1)",
                                              zIndex: 50,
                                            }}>
                                            <input
                                              type="range"
                                              min={1}
                                              max={20}
                                              step={1}
                                              value={strokeSliderValue}
                                              onChange={(e) => {
                                                const val = parseInt(
                                                  e.target.value
                                                );
                                                setStrokeSliderValue(val);

                                                const obj =
                                                  canvas?.getActiveObject();
                                                if (obj) {
                                                  if (!obj.stroke)
                                                    obj.set("stroke", "#000");
                                                  obj.set("strokeWidth", val);
                                                  canvas.requestRenderAll();
                                                }
                                              }}
                                              style={{ width: "100%" }}
                                            />
                                          </div>
                                        )}
                                      </motion.div>
                                    );
                                  }

                                  // 🔹 Default Buttons
                                  return (
                                    <motion.button
                                      key={key}
                                      onClick={onClick}
                                      style={toolBtnStyle}
                                      initial={{ opacity: 0, scale: 0 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0 }}
                                      transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 30,
                                        delay,
                                      }}>
                                      {icon}
                                    </motion.button>
                                  );
                                }
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/*Add Asset Popover */}
        {showAddPopover && (
          <div
            ref={addPopoverRef}
            style={{
              position: "absolute",
              zIndex: 50,
              top: `${popoverPos.top}px`,
              left: `${popoverPos.left}px`,
              backgroundColor: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: "0.25rem",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              padding: "0.5rem",
            }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.5rem",
              }}>
              {/* TEXT */}
              <button
                style={{
                  width: "5rem",
                  height: "5rem",
                  backgroundColor: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.25rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e5e7eb")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "#fff")
                }
                onClick={() => {
                  addText("New Text");
                  setShowAddPopover(false);
                }}>
                Text
              </button>

              {/* SHAPES (Dropdown) */}
              <div style={{ position: "relative" }}>
                <button
                  style={{
                    width: "5rem",
                    height: "5rem",
                    backgroundColor: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.25rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                  onClick={() => setShowShapeOptions((v) => !v)}>
                  Shape
                </button>

                {showShapeOptions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "5.5rem",
                      left: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.25rem",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      padding: "0.5rem",
                      width: "10rem",
                      zIndex: 50,
                    }}>
                    {["square", "rectangle", "circle"].map((shape) => (
                      <div
                        key={shape}
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "0.25rem",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e5e7eb")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                        onClick={() => {
                          addShape(shape);
                          setShowShapeOptions(false);
                          setShowAddPopover(false);
                        }}>
                        {shape.charAt(0).toUpperCase() + shape.slice(1)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* IMAGE (Dropdown) */}
              <div style={{ position: "relative" }}>
                <button
                  style={{
                    width: "5rem",
                    height: "5rem",
                    backgroundColor: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.25rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                  onClick={() => setShowImageInput((v) => !v)}>
                  Image
                </button>

                {showImageInput && (
                  <div
                    style={{
                      position: "absolute",
                      top: "5.5rem",
                      left: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.25rem",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      padding: "0.5rem",
                      width: "15rem",
                      zIndex: 50,
                    }}>
                    <input
                      type="text"
                      placeholder="Paste image URL"
                      style={{
                        width: "90%",
                        fontSize: "0.875rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.25rem",
                        padding: "0.25rem 0.5rem",
                        marginBottom: "0.5rem",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.target.value) {
                          addImageFromURL(e.target.value);
                          setShowImageInput(false);
                          setShowAddPopover(false);
                        }
                      }}
                    />

                    <button
                      style={{
                        width: "100%",
                        backgroundColor: "#e5e7eb",
                        border: "none",
                        fontSize: "0.875rem",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.25rem",
                        cursor: "pointer",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = "#d1d5db")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e5e7eb")
                      }
                      onClick={() => fileInputRef.current.click()}>
                      Upload Image From Your Computer
                    </button>

                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          addImageFromFile(file);
                          setShowImageInput(false);
                          setShowAddPopover(false);
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              {/* SPECIAL (Dropdown) */}
              <div style={{ position: "relative" }}>
                <button
                  style={{
                    width: "5rem",
                    height: "5rem",
                    backgroundColor: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.25rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                  onClick={() => setShowSpecialOptions((v) => !v)}>
                  Special
                </button>

                {showSpecialOptions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "5.5rem",
                      left: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.25rem",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      padding: "0.5rem",
                      width: "12rem",
                      zIndex: 50,
                    }}>
                    {["ellipse", "polygon", "line"].map((shape) => (
                      <div
                        key={shape}
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "0.25rem",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e5e7eb")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                        onClick={() => {
                          addShape(shape);
                          setShowSpecialOptions(false);
                          setShowAddPopover(false);
                        }}>
                        {shape.charAt(0).toUpperCase() + shape.slice(1)}
                      </div>
                    ))}

                    <div
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.25rem",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                      onClick={() => {
                        startDrawingPath();
                        setShowSpecialOptions(false);
                        setShowAddPopover(false);
                      }}>
                      Path (Draw)
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/*Are you sure? Saving -  Popover */}
        {showUnsavedPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.4)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}>
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                maxWidth: "300px",
                textAlign: "center",
              }}>
              <h3 style={{ marginBottom: "12px" }}>Unsaved Changes</h3>
              <p style={{ marginBottom: "16px" }}>
                You have unsaved changes. Do you want to save before closing?
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}>
                <button
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    handleSaveShapes(); // Save
                    setShowUnsavedPopup(false);
                    onClose(); // Close after saving
                  }}>
                  Save & Close
                </button>
                <button
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#e5e7eb",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowUnsavedPopup(false);
                    onClose(); // Close without saving
                  }}>
                  Discard
                </button>
                <button
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#f87171",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowUnsavedPopup(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotateCanvas;
